import { Trial, UpdateStatusEvent } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import IsLoading from "@/components/layouts/IsLoading";
import SetLinks from "@/components/layouts/SetLinks";
import Show from "@/components/layouts/Show";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationInstitutionCreateLink, ConfigurationTrialCreateLink, NotesAndQueriesInstitutionCreateLink, NotesAndQueriesTrialCreateLink } from "@/contexts/layouts/LinkContext";
import useConfigurationTrial from "@/hooks/data/useConfigurationTrial";
import useLockedEventStatusUpdate from "@/hooks/data/useLockedEventStatusUpdate";
import useStatusUpdate from "@/hooks/data/useStatusUpdate";
import useTrialNotesAndQueries from "@/hooks/data/useTrialNotesAndQueries";
import useTrialStatuses from "@/hooks/data/useTrialStatuses";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItemButton, ListItemIcon, ListItemText, Paper, Typography, styled } from "@mui/material";
import { FC, useCallback, useContext, useState } from "react";
import { Link } from "react-router-dom";

export const StatusListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6)
}));

export const StatusListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

export const StatusListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const ConfigurationUtilities: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroups } = useContext(MasterGroupsContext);
    const { data: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { data: institutions } = useContext(InstitutionsContext);
    const { data: configurationTrial, loading: configurationTrialLoading, validating: configurationTrialValidating } = useConfigurationTrial(trial?.id ? { trialId: trial?.id } : undefined, { revalidateOnFocus: false });


    const [open, setOpen] = useState(false);

    
    const { idMap: trialStatuses, loading: trialStatusesLoading, validating: trialStatusesValidating, refresh: trialStatusesRefresh } = useTrialStatuses();


    const updateStatuses = useCallback(async (event: UpdateStatusEvent) => {
        await trialStatusesRefresh()
    }, [trialStatusesRefresh]);

    const {processing, queueUpdate, error} = useLockedEventStatusUpdate(
        updateStatuses,
        updateStatuses
    );

    const queueStatusUpdate = useCallback(async (item: Trial) => {
        await queueUpdate({ trialId: item.id });
        await trialStatusesRefresh();
    }, [queueUpdate, trialStatusesRefresh])

    const loading = configurationTrialLoading;
    const validating = configurationTrialValidating;

    const trialUpdating = !trial?.id || !trialStatuses?.[trial.id] || trialStatuses?.[trial.id].updating || processing;

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationTrialCreateLink} 
                createInstitutionLink={ConfigurationInstitutionCreateLink} 
                institutionLinkEnabled={false} 
                participantLinkEnabled={false} 
            />
            <IsLoading name="configuration-page" loading={loading} validating={validating} />
            <Show show={!loading}>
                <Typography variant="h1" gutterBottom>
                    Configuration &mdash; Utilities
                </Typography>
                <Paper>
                    <List disablePadding>
                        <StatusListItemButton 
                            disabled={trialUpdating}
                            onClick={() => setOpen(true)}
                        >
                            <StatusListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.utilities} />
                            </StatusListItemIcon>
                            <StatusListItemText primary="Update locked events to be verified" />
                        </StatusListItemButton>
                    </List>
                </Paper>
                <Dialog
                    //onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                    maxWidth="xs"
                    fullWidth
                    scroll="paper"
                >
                    <DialogTitle>
                        Update Locked Events?
                    </DialogTitle>
                    <DialogContent dividers>
                        Are you sure you wish to mark all events that are <strong>locked</strong> in OpenClinica as <strong>verified</strong> in the Source Data Verification system?
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (trial) {
                                    queueStatusUpdate(trial)
                                }

                                setOpen(false);
                            }}
                        >
                            Ok
                        </Button>
                        <Button
                            variant="contained"
                            color="inherit"
                            onClick={() => setOpen(false)}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            </Show>
        </>
    )
};

export default ConfigurationUtilities;